import { useCallback, useEffect, useId, useRef, useState } from "react";
import {
  AlertTriangle,
  ClipboardList,
  FileText,
  Link2,
  Loader2,
  Share2,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSelectionMode } from "@/components/records/selection-mode/selection-mode-provider";
import { FloatingActionButton } from "@/components/common/floating-action-button/floating-action-button";
import {
  NAV_HEIGHT_PX,
  RAIL_WIDTH_VAR,
} from "@/components/common/bottom-nav/nav-constants";
import {
  useCreateInviteLink,
  useInviteLinks,
} from "@/hooks/use-invite-links";
import { useOnlineStatus } from "@/offline/online-status";
import { cn } from "@/lib/utils";

/**
 * Constants surfaced from [[selection-mode.spec]] § Behavior § Constants.
 * `MAX_SHARE_COUNT` is the hard cap for the iOS/Android native share sheet
 * — above 20 the OS share sheet starts behaving badly. `LARGE_BATCH_THRESHOLD`
 * is the softer threshold above which we offer "Share Album Link" as a
 * sensible alternative.
 */
const LARGE_BATCH_THRESHOLD = 10;
const MAX_SHARE_COUNT = 20;
/**
 * Most records the Issues button will raise in one go. A batch is one POST per
 * issue until the bulk endpoint exists (FE-DEBT-032), so this bounds what a
 * jobsite connection has to get through while someone waits on the sheet.
 */
export const MAX_ISSUE_BATCH = 25;
/** How long the over-limit bubble stays after a tap. */
const LIMIT_HINT_MS = 3000;

interface BulkActionBarProps {
  /** Whether the current user has `report:Create` permission. The Report
   * button is hidden entirely (per [[permissions.spec]]) when false. */
  canCreateReport: boolean;
  /** Whether the current user has `issue:Create` permission. The Issues
   * button is hidden entirely (per [[permissions.spec]]) when false. */
  canCreateIssue: boolean;
  /** The project id, needed for the album-link share fallback. */
  projectId: string;
}

/**
 * The bulk action bar that replaces the bottom navbar when selection mode
 * is active. Lives at the layout level (rendered by `BottomNav`) so it can
 * cover the full viewport width independent of which child route is
 * mounted. Per [[selection-mode.spec]] § Layout.
 */
export function BulkActionBar({
  canCreateReport,
  canCreateIssue,
  projectId,
}: BulkActionBarProps) {
  const {
    isSelectionMode,
    isReportMode,
    selectedCount,
    isAllSelected,
    isSelectingAll,
    isDeletingRecords,
    isSharing,
    exitReportMode,
    triggerDelete,
    triggerReport,
    triggerIssues,
    triggerShare,
    triggerSelectAll,
    triggerUnselectAll,
  } = useSelectionMode();

  const isOnline = useOnlineStatus();

  const [isLargeBatchOpen, setIsLargeBatchOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [canShare, setCanShare] = useState(false);

  // navigator.share is only available in HTTPS contexts on supporting
  // browsers (mostly mobile Safari + Chrome on Android). When absent we hide
  // the Share button entirely per [[selection-mode.spec]] § Edge Cases.
  useEffect(() => {
    setCanShare(typeof navigator !== "undefined" && !!navigator.share);
  }, []);

  // Lazy-fetch invite links only when the large-batch dialog is open and
  // the user might tap "Share Album Link" — avoids hitting the endpoint on
  // every selection-mode entry.
  const { data: inviteLinks } = useInviteLinks(projectId, isLargeBatchOpen);
  const createInviteLink = useCreateInviteLink(projectId);
  const [isSharingLink, setIsSharingLink] = useState(false);

  const handleShareClick = useCallback(() => {
    if (selectedCount > LARGE_BATCH_THRESHOLD) {
      setIsLargeBatchOpen(true);
    } else {
      triggerShare();
    }
  }, [selectedCount, triggerShare]);

  const handleShareAlbumLink = useCallback(async () => {
    setIsSharingLink(true);
    try {
      const existing = inviteLinks?.find(
        (l) => l.link_type === "view" && l.is_active,
      );
      let shareUrl = existing?.share_url;
      if (!shareUrl) {
        const created = await createInviteLink.mutateAsync("view");
        shareUrl = created.share_url;
      }
      if (!shareUrl) {
        // Both the existing-link query AND the create-link mutation
        // returned without a usable URL — likely a malformed API response
        // or schema regression. Surface inline and bail; do NOT degrade
        // to `window.location.href` because that would share whichever
        // route the user happens to be on (possibly behind auth, possibly
        // still carrying `?mode=report`) instead of an actual album link.
        // [[error-reporting.spec]] § "Never log-and-swallow".
        toast("Couldn't generate share link");
        return;
      }
      if (navigator.share) {
        try {
          await navigator.share({ url: shareUrl, title: "Otus Album" });
        } catch (error) {
          // AbortError = user dismissed the share sheet — not a failure,
          // not user-visible feedback either.
          if (error instanceof Error && error.name === "AbortError") {
            return;
          }
          // Anything else: surface inline (toast). Per
          // [[error-reporting.spec]] § "Never log-and-swallow" — without
          // the toast the user gets no signal that the share failed.
          toast("Couldn't open the share sheet");
        }
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        toast("Album link copied");
      } else {
        // No share API and no clipboard — extremely rare (very old WebViews
        // or hardened privacy contexts). Tell the user the link couldn't
        // go out so they don't think the action quietly succeeded.
        toast("Couldn't share the album link on this device");
      }
    } finally {
      setIsSharingLink(false);
      setIsLargeBatchOpen(false);
    }
  }, [createInviteLink, inviteLinks]);

  const handleSharePhotosAnyway = useCallback(() => {
    setIsLargeBatchOpen(false);
    triggerShare();
  }, [triggerShare]);

  const handleGenerateReport = useCallback(() => {
    setIsLargeBatchOpen(false);
    triggerReport();
  }, [triggerReport]);

  if (!isSelectionMode) return null;

  const showShareButton = !isReportMode && canShare;
  const showReportButton = canCreateReport;
  // Hidden in report mode like Share: a session entered to build a report
  // stays on that one job. See [[selection-mode.spec]] US-007.
  const showIssuesButton = canCreateIssue && !isReportMode;

  // Report generation needs a server round-trip — it can't be queued offline.
  // The offline hint takes precedence over the share-cap hint in the center
  // column; it's the more fundamental blocker.
  const centerHint =
    !isOnline && showReportButton
      ? "Offline — reconnect to generate"
      : !isReportMode && selectedCount > MAX_SHARE_COUNT
        ? `Max ${MAX_SHARE_COUNT} to share`
        : null;

  return (
    <>
      {/* Select All / Unselect All FAB — floats above the action bar.
          z-50 keeps it in the same stacking context as the bar itself. */}
      {/* On Issues, Select All first loads the rest of the view, so it can be
          busy; see [[selection-mode.spec]] § Select All on Issues. */}
      <FloatingActionButton
        zIndexClassName="z-50"
        onClick={isAllSelected ? triggerUnselectAll : triggerSelectAll}
        disabled={isSelectingAll}
        aria-busy={isSelectingAll || undefined}
      >
        {isSelectingAll && (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        )}
        {isAllSelected ? "Unselect All" : "Select All"}
      </FloatingActionButton>

      {/* Action bar replaces the phone's bottom navbar; from 768px it runs
          beside the rail, so the inline `left` (the rail's width, 0 on a
          phone) overrides inset-x-0's. Animate in from below. */}
      <div
        className="animate-in slide-in-from-bottom-4 fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background duration-300"
        style={{
          left: `var(${RAIL_WIDTH_VAR}, 0px)`,
          height: `calc(${NAV_HEIGHT_PX}px + env(safe-area-inset-bottom, 0px))`,
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        <div className="flex h-full items-center gap-2 px-4">
          {/* Left: Delete (or Cancel in report mode) */}
          <div className="flex flex-none justify-start">
            {isReportMode ? (
              // The session's only Cancel: the header hides its own in report
              // mode. Returns to Reports when the session started there. See
              // [[selection-mode.spec]] § Report mode.
              <Button variant="ghost" onClick={exitReportMode}>
                Cancel
              </Button>
            ) : (
              <Button
                variant="ghost"
                className="text-destructive"
                onClick={() => setConfirmDeleteOpen(true)}
                disabled={isDeletingRecords || selectedCount === 0}
              >
                {isDeletingRecords ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Delete
              </Button>
            )}
          </div>

          {/* Center: count + share-cap / offline hint */}
          <div className="flex flex-1 flex-col items-center justify-center">
            <span className="text-sm font-medium">{selectedCount}</span>
            {centerHint && (
              <span className="text-[10px] text-muted-foreground">
                {centerHint}
              </span>
            )}
          </div>

          {/* Right: Share + Issues + Report */}
          <div className="flex flex-none justify-end gap-1">
            {showShareButton && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleShareClick}
                disabled={
                  selectedCount === 0 ||
                  selectedCount > MAX_SHARE_COUNT ||
                  isSharing
                }
                aria-label="Share selected photos"
              >
                {isSharing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Share2 className="h-4 w-4" />
                )}
              </Button>
            )}
            {showIssuesButton && (
              <IssuesButton
                selectedCount={selectedCount}
                onRaise={triggerIssues}
              />
            )}
            {showReportButton && (
              <Button
                variant="default"
                onClick={triggerReport}
                disabled={selectedCount === 0 || !isOnline || isSelectingAll}
                // The visible word alone is ambiguous to a screen reader; the
                // name still contains it, so voice control can say "Report".
                aria-label="Generate Report"
              >
                <FileText className="h-4 w-4" aria-hidden="true" />
                Report
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Confirm bulk delete */}
      <AlertDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-destructive/15 bg-destructive/[0.08]">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <AlertDialogTitle className="text-center">
              Delete {selectedCount} photo{selectedCount === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              This cannot be undone. The photo
              {selectedCount === 1 ? "" : "s"} will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/85"
              onClick={() => {
                triggerDelete();
                setConfirmDeleteOpen(false);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Large-batch share dialog — offered when the user tries to share
          more than LARGE_BATCH_THRESHOLD photos. Three options per spec
          § Large batch share dialog. */}
      <Dialog open={isLargeBatchOpen} onOpenChange={setIsLargeBatchOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Large batch selected</DialogTitle>
            <DialogDescription>
              You&apos;ve selected {selectedCount} photos! Consider sharing
              the album link or generating a report instead.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col gap-2 sm:flex-col">
            {showReportButton && (
              <Button onClick={handleGenerateReport} disabled={!isOnline}>
                <FileText className="h-4 w-4" />
                Generate Report
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={handleShareAlbumLink}
              disabled={isSharingLink}
            >
              {isSharingLink ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Link2 className="h-4 w-4" />
              )}
              Share Album Link
            </Button>
            <Button variant="ghost" onClick={handleSharePhotosAnyway}>
              Share photos anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface IssuesButtonProps {
  selectedCount: number;
  onRaise: () => void;
}

/**
 * The Issues action, capped at {@link MAX_ISSUE_BATCH}.
 *
 * Over the cap it is `aria-disabled` rather than `disabled`: a disabled
 * button gets no events, so a phone user could never find out why it went
 * grey. It keeps the disabled look, but a tap shows the limit bubble instead of
 * opening the sheet, and hover or keyboard focus shows it while they last. See
 * [[selection-mode.spec]] § Issues button limit.
 */
function IssuesButton({ selectedCount, onRaise }: IssuesButtonProps) {
  const isOverLimit = selectedCount > MAX_ISSUE_BATCH;
  const [isHintRequested, setIsHintRequested] = useState(false);
  const hideTimer = useRef<number | undefined>(undefined);
  const hintId = useId();

  useEffect(() => () => window.clearTimeout(hideTimer.current), []);

  // Derived, so dropping back under the cap hides the bubble with no effect.
  const isHintVisible = isOverLimit && isHintRequested;

  const showHint = () => {
    window.clearTimeout(hideTimer.current);
    setIsHintRequested(true);
  };
  const hideHint = () => {
    window.clearTimeout(hideTimer.current);
    setIsHintRequested(false);
  };

  const handleClick = () => {
    if (!isOverLimit) {
      onRaise();
      return;
    }
    // A tap has no "leave" to close on, so it times out instead.
    showHint();
    hideTimer.current = window.setTimeout(
      () => setIsHintRequested(false),
      LIMIT_HINT_MS,
    );
  };

  return (
    <div className="relative">
      <Button
        variant="outline"
        onClick={handleClick}
        disabled={selectedCount === 0}
        aria-disabled={isOverLimit || undefined}
        aria-describedby={isOverLimit ? hintId : undefined}
        aria-label="Raise issues"
        onPointerEnter={(event) => {
          if (event.pointerType === "mouse") showHint();
        }}
        onPointerLeave={(event) => {
          if (event.pointerType === "mouse") hideHint();
        }}
        onFocus={showHint}
        onBlur={hideHint}
        className={cn(
          isOverLimit &&
            // The disabled look without `disabled`: no hover tint, no press.
            "cursor-not-allowed opacity-50 hover:bg-background hover:text-foreground active:scale-100 dark:hover:bg-input/30",
        )}
      >
        <ClipboardList className="h-4 w-4" aria-hidden="true" />
        Issues
      </Button>
      {/* Always in the DOM while over the cap so `aria-describedby` resolves;
          only its visibility follows hover, focus and taps. */}
      {isOverLimit && (
        <div
          id={hintId}
          role="tooltip"
          data-state={isHintVisible ? "open" : "closed"}
          className={cn(
            "pointer-events-none absolute right-0 bottom-full mb-2 w-max max-w-[220px] rounded-md bg-foreground px-2.5 py-1.5 text-xs leading-snug text-background shadow-md transition-opacity duration-150",
            isHintVisible ? "opacity-100" : "opacity-0",
          )}
        >
          Max limit hit. Reduce to {MAX_ISSUE_BATCH} records and try again.
        </div>
      )}
    </div>
  );
}
